import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Checkbox,
  classes,
  Link,
  PrimaryButton,
} from '@bloomberg/media-ui-core';
import { ExitThin } from '@bloomberg/media-ui-icons';

import { Watchlist, WatchlistSaveForm } from '../../types';
import { CreateWatchlist } from '../CreateWatchlist';

import styles from './AddToWatchlist.module.css';

export const DEFAULT_WATCHLIST_NAME = 'My watchlist';
const DEFAULT_WATCHLIST = {
  isFollowing: true,
  isFull: false,
  watchlistName: DEFAULT_WATCHLIST_NAME,
  watchlistId: DEFAULT_WATCHLIST_NAME,
} as const;

export type AddToWatchlistProps = {
  canAddWatchlist: boolean;
  onClose: () => void;
  onSave: (saveForm: WatchlistSaveForm) => void;
  onCreateNewWatchlist: (watchlistName: string) => Promise<number | null>;
  watchlists: Watchlist[];
  trackClick?: (eventAction: string, eventLabel: string) => void;
};

const WATCHLIST_OVERFLOW_LIMIT = 4;

type AddToWatchlistWatchlistId = 'My watchlist' | number;
type AddToWatchlistWatchlist = Omit<Watchlist, 'watchlistId'> & {
  watchlistId: AddToWatchlistWatchlistId;
};

export const AddToWatchlist = ({
  canAddWatchlist,
  onClose,
  onSave,
  onCreateNewWatchlist,
  watchlists,
  trackClick,
}: AddToWatchlistProps) => {
  const [addedNewWatchlist, setAddedNewWatchlist] = useState(false);
  const [_watchlists, setWatchlists] = useState<AddToWatchlistWatchlist[]>(
    watchlists.length ? watchlists : [DEFAULT_WATCHLIST]
  );
  const [watchlistIds, setWatchlistIds] = useState<Record<string, boolean>>(
    watchlists.length ? {} : { [DEFAULT_WATCHLIST_NAME]: true }
  );
  const [showAddNewWatchlist, setShowAddNewWatchlist] = useState(false);
  const watchlistContainer = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = watchlistContainer.current;

    if (!container) {
      return;
    }

    if (!addedNewWatchlist) {
      return;
    }

    container.scroll({
      top: container.scrollHeight,
      behavior: 'smooth',
    });

    setAddedNewWatchlist(false);
  }, [addedNewWatchlist]);

  const toggleWatchlist = useCallback(
    (id: AddToWatchlistWatchlistId, isChecked: boolean) => {
      setWatchlistIds((prevWatchlistIds) => {
        if (Object.hasOwn(prevWatchlistIds, id)) {
          const { [id]: _renovedId, ...nextWatchlistIds } = prevWatchlistIds;

          return nextWatchlistIds;
        }

        return { ...prevWatchlistIds, [id]: isChecked };
      });
    },
    []
  );

  const handleCreateNewWatchlist = useCallback(
    async (watchlistName: string) => {
      const newWatchlistId = await onCreateNewWatchlist(watchlistName);

      if (!newWatchlistId) {
        return;
      }

      setWatchlists((prevWatchlists) => [
        ...prevWatchlists,
        {
          isFollowing: true,
          isFull: false,
          watchlistName,
          watchlistId: newWatchlistId,
        },
      ]);
      setWatchlistIds((prevWatchlistIds) => ({
        ...prevWatchlistIds,
        [newWatchlistId]: true,
      }));
      setShowAddNewWatchlist(false);
      setAddedNewWatchlist(true);
    },
    [onCreateNewWatchlist, toggleWatchlist]
  );

  const handleOnCloseClick = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const handleSave = useCallback(() => {
    const save: WatchlistSaveForm = {
      createDefaultWatchlist: false,
      adds: [],
      deletes: [],
    };

    Object.entries(watchlistIds).forEach(([id, isChecked]) => {
      if (id === DEFAULT_WATCHLIST_NAME) {
        save.createDefaultWatchlist = true;

        return;
      }

      const watchlistId = parseInt(id, 10);

      if (isNaN(watchlistId)) {
        return;
      }

      if (isChecked) {
        save.adds.push(watchlistId);

        return;
      }

      save.deletes.push(watchlistId);
    });

    onSave?.(save);
  }, [onSave, watchlistIds]);

  const onWatchlistLinkClick = useCallback(() => {
    trackClick?.('click', 'open-list-quick-add');
  }, [trackClick]);

  return (
    <div className={styles.addToWatchlist}>
      <div className={styles.header}>
        <p className={styles.confirmationText}>Add to watchlist</p>
        <button
          className={styles.showContainer}
          onClick={handleOnCloseClick}
          type="button"
        >
          <ExitThin />
        </button>
      </div>
      <div>
        <div
          ref={watchlistContainer}
          className={classes([
            styles.watchlists,
            _watchlists.length > WATCHLIST_OVERFLOW_LIMIT && styles.overflow,
          ])}
        >
          {_watchlists.map(
            ({ isFollowing, isFull, watchlistId, watchlistName }) => {
              const disableWatchlist = isFull && !isFollowing;
              const isDefaultWatchlist = watchlistId === DEFAULT_WATCHLIST_NAME;

              return (
                <div className={styles.watchlist} key={watchlistId}>
                  <div
                    className={classes([
                      styles.watchlistHeader,
                      disableWatchlist && styles.watchlistDisabled,
                    ])}
                  >
                    <Checkbox
                      className={styles.watchlistToggle}
                      defaultChecked={isFollowing}
                      disabled={disableWatchlist}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        toggleWatchlist(watchlistId, e.target.checked)
                      }
                      id={watchlistId.toString()}
                    />
                    <label
                      htmlFor={watchlistId.toString()}
                      className={styles.watchlistName}
                    >
                      {/* using pseudo element to append (full) doesn't work with name truncation */}
                      {disableWatchlist
                        ? `${watchlistName} (full)`
                        : watchlistName}
                    </label>
                  </div>
                  {!isDefaultWatchlist && (
                    <Link
                      onClick={onWatchlistLinkClick}
                      className={styles.watchlistOpenLink}
                      href={`/markets/watchlist?watchlist=${watchlistId}`}
                      target="_blank"
                    >
                      Open
                    </Link>
                  )}
                </div>
              );
            }
          )}
        </div>
        {canAddWatchlist && (
          <CreateWatchlist
            onCreate={handleCreateNewWatchlist}
            showAddNewWatchlist={showAddNewWatchlist}
            setShowAddNewWatchlist={setShowAddNewWatchlist}
          />
        )}
        {!showAddNewWatchlist && (
          <PrimaryButton
            className={styles.saveButton}
            onClick={handleSave}
            disabled={!Object.keys(watchlistIds).length}
            size="small"
            version="phoenix"
          >
            Save
          </PrimaryButton>
        )}
      </div>
    </div>
  );
};
